import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { startRegistration } from '@simplewebauthn/browser';
import toast from 'react-hot-toast';

import { PrimaryBlackButton, PrimaryWhiteButton } from '../../../components/Button/Buttons';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import { Card, CardTitle, Hint, Input, Stack } from '../../../components/Layout/PagePieces';
import Page from '../../../components/Page/Page';
import useFreshPasskey from '../../../hooks/auth/passkeys/useFreshPasskey';
import usePasskeys from '../../../hooks/auth/passkeys/usePasskeys';
import { browserSupportsWebAuthn, passkeyErrorMessage, UNSUPPORTED } from '../../../hooks/auth/passkeys/webauthn';
import { tableFormatDateTimeNG } from '../../../utils/utils';
import RecoveryCodes from '../../Auth/Passkeys/RecoveryCodes';
import { deviceName } from '../../Auth/SignIn/PasskeyStep';

// Your passkeys (ops spec K step 11): add one on another device, rename,
// remove (never the last), and a new set of recovery codes. Adding,
// removing and new codes need a passkey check from the last 5 minutes; the
// page asks for one when the server says so.
const Passkeys = () => {
  const { list, busy, fetchPasskeys, rename } = usePasskeys();
  const { call, fresh } = useFreshPasskey();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [working, setWorking] = useState(false);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const supported = browserSupportsWebAuthn();

  useEffect(() => {
    fetchPasskeys();
    // eslint-disable-next-line
  }, []);

  const add = async () => {
    setWorking(true);
    try {
      const optionsJSON = await fresh<any>({ url: 'api/v1/admin/passkeys/register/options', method: 'post' });
      const credential = await startRegistration({ optionsJSON });
      await call({ url: 'api/v1/admin/passkeys/register/verify', method: 'post', data: { credential, name: deviceName() } });
      toast.success('Passkey added');
      fetchPasskeys();
    } catch (err) {
      toast.error(passkeyErrorMessage(err));
    } finally {
      setWorking(false);
    }
  };

  const removePasskey = async (id: string, name: string) => {
    if (!window.confirm(`Remove “${name}”?`)) return;
    setWorking(true);
    try {
      await fresh({ url: `api/v1/admin/passkeys/${id}`, method: 'delete' });
      toast.success('Passkey removed');
      fetchPasskeys();
    } catch (err) {
      toast.error(passkeyErrorMessage(err));
    } finally {
      setWorking(false);
    }
  };

  const newCodes = async () => {
    if (!window.confirm('Make a new set of recovery codes? Your old codes will stop working.')) return;
    setWorking(true);
    try {
      setCodes(await fresh<string[]>({ url: 'api/v1/admin/passkeys/recovery-codes', method: 'post' }));
      fetchPasskeys();
    } catch (err) {
      toast.error(passkeyErrorMessage(err));
    } finally {
      setWorking(false);
    }
  };

  const passkeys = list?.passkeys ?? [];
  return (
    <Page title='Hethera | Passkeys'>
      <PageLayout>
        <Stack>
          <PageHeader title='Passkeys' className='borderless no-bottom-padding' />
          {codes ? (
            <Card>
              <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
            </Card>
          ) : (
            <>
              <Card>
                <CardTitle>Your passkeys</CardTitle>
                <Hint>
                  You sign in with your password and one of these. Add one on a second device (a phone and a laptop) so
                  losing one doesn’t lock you out.
                </Hint>
                {!supported && <Hint>{UNSUPPORTED}</Hint>}
                {passkeys.map((p) => (
                  <Item key={p.id}>
                    {editing?.id === p.id ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          rename(p.id, editing.name);
                          setEditing(null);
                        }}
                      >
                        <Input value={editing.name} maxLength={60} onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus />
                        <PrimaryWhiteButton type='submit'>Save</PrimaryWhiteButton>
                      </form>
                    ) : (
                      <div>
                        <strong>{p.name}</strong>
                        <small>
                          Added {tableFormatDateTimeNG(p.created_at)} · {p.last_used_at ? `last used ${tableFormatDateTimeNG(p.last_used_at)}` : 'not used yet'}
                          {p.backed_up ? ' · synced' : ''}
                        </small>
                      </div>
                    )}
                    <Buttons>
                      <PrimaryWhiteButton type='button' onClick={() => setEditing({ id: p.id, name: p.name })}>
                        Rename
                      </PrimaryWhiteButton>
                      <PrimaryWhiteButton
                        type='button'
                        disabled={busy || working || passkeys.length <= 1}
                        title={passkeys.length <= 1 ? 'Add another passkey before removing this one' : undefined}
                        onClick={() => removePasskey(p.id, p.name)}
                      >
                        Remove
                      </PrimaryWhiteButton>
                    </Buttons>
                  </Item>
                ))}
                <span>
                  <PrimaryBlackButton
                    type='button'
                    disabled={working || !supported || passkeys.length >= (list?.max_passkeys ?? 5)}
                    onClick={add}
                  >
                    {working ? 'Waiting for your passkey…' : 'Add a passkey on this device'}
                  </PrimaryBlackButton>
                </span>
              </Card>
              <Card>
                <CardTitle>Recovery codes</CardTitle>
                <Hint>
                  {list?.recovery_codes_left ?? 0} unused code{list?.recovery_codes_left === 1 ? '' : 's'} left. Each lets you sign
                  in once without a passkey, to add a new one.
                </Hint>
                <span>
                  <PrimaryWhiteButton type='button' disabled={working || !supported} onClick={newCodes}>
                    Make new recovery codes
                  </PrimaryWhiteButton>
                </span>
              </Card>
            </>
          )}
        </Stack>
      </PageLayout>
    </Page>
  );
};

export default Passkeys;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 0;
  border-bottom: 1px solid #efece4;

  div {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  small {
    color: ${({ theme }) => theme.palette.grayTextColor};
  }
  form {
    display: flex;
    gap: 8px;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;
